import { isError } from 'lodash';
import { memo, useCallback } from 'react';
import { object } from 'yup';

import { type Board } from '@air/api/types';
import { InlineInput, type InlineInputProps } from '@air/component-inline-input';

import { BoardTitleValidation } from '~/swr-hooks/boards/useCreateNewBoard';
import { useUpdateBoard } from '~/swr-hooks/boards/useUpdateBoard';

const BoardTitleValidationScheme = object().shape({
  ['title']: BoardTitleValidation,
});

const validateBoardName = (value: string) => {
  try {
    BoardTitleValidationScheme.validateSync({ title: value });
    return undefined;
  } catch (error) {
    return isError(error) ? error.message : 'Unknown';
  }
};

export type WorkspaceSidebarBoardsListItemRenameInputProps = {
  board: Board;
  onCancel: () => void;
  onSubmit: () => void;
};

export const WorkspaceSidebarBoardsListItemRenameInput = memo(
  ({ board, onCancel, onSubmit: onSubmitProp }: WorkspaceSidebarBoardsListItemRenameInputProps) => {
    const { updateBoard } = useUpdateBoard();

    const onSubmit = useCallback<Required<InlineInputProps>['onSubmit']>(
      (value) => {
        updateBoard.mutate({
          board,
          trackLocation: 'side-nav-tree-item',
          values: { title: value },
        });

        onSubmitProp();
      },
      [board, onSubmitProp, updateBoard],
    );

    return (
      <InlineInput
        allowNewLines={false}
        className="z-40 -ml-2"
        data-testid="WORKSPACE_SIDEBAR_BOARD_RENAME_INPUT"
        defaultValue={board.title}
        inputClassName="-ml-px"
        label="Board name"
        onCancel={onCancel}
        onSubmit={onSubmit}
        rows={1}
        validate={validateBoardName}
      />
    );
  },
);

WorkspaceSidebarBoardsListItemRenameInput.displayName = 'WorkspaceSidebarBoardsListItemRenameInput';
